import { useEffect, useState } from "react";

import { Button, Divider, Stack, Typography } from "@mui/material";

import { providersForEmail, startSsoLogin, type PublicProvider, type SsoKind } from "@/api/sso";

/** Wordmarks stay text: shipping the real logos means shipping their licences. */
const KIND_LABEL: Record<SsoKind, string> = {
  GOOGLE: "Google",
  MICROSOFT: "Microsoft",
  OIDC: "SSO",
};

/** Where the browser comes back to. Must match the API's `_redirect_uri`. */
export const SSO_LOGIN_CALLBACK_PATH = "/auth/sso/callback";

type Props = {
  /** The address typed in the form — providers are resolved from the account. */
  email: string;
  disabled?: boolean;
  nextPath?: string;
};

export function SsoButtons({ email, disabled, nextPath }: Props) {
  const [providers, setProviders] = useState<PublicProvider[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const normalized = email.trim().toLowerCase();
  const looksLikeEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized);

  useEffect(() => {
    if (!looksLikeEmail) {
      setProviders([]);
      return;
    }
    let cancelled = false;
    // Debounced: the field is queried while the user is still typing.
    const timer = window.setTimeout(() => {
      providersForEmail(normalized)
        .then((list) => {
          if (!cancelled) setProviders(list);
        })
        .catch(() => {
          if (!cancelled) setProviders([]);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [normalized, looksLikeEmail]);

  if (providers.length === 0) return null;

  const go = async (p: PublicProvider) => {
    setBusyId(p.id);
    setError(null);
    try {
      const { authorization_url } = await startSsoLogin(p.id, nextPath);
      window.location.assign(authorization_url);
    } catch {
      setError("Connexion SSO indisponible pour le moment.");
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Divider>
        <Typography variant="caption" color="text.secondary">
          ou
        </Typography>
      </Divider>
      {providers.map((p) => (
        <Button
          key={p.id}
          variant="outlined"
          size="large"
          fullWidth
          disabled={disabled || busyId !== null}
          onClick={() => void go(p)}
          sx={{ py: 1.1, textTransform: "none" }}
        >
          {busyId === p.id
            ? "Redirection…"
            : `Continuer avec ${p.display_name || KIND_LABEL[p.kind]}`}
        </Button>
      ))}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
}
